import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { groupPath } from '../lib/groupPath';
import { normalizeJoinCode } from '../lib/joinCode';
import { useStore } from '../store/useStore';
import { Login } from './Login';

type Phase = 'joining' | 'joined' | 'failed';

/**
 * Landing page for a `/join/CODE` link. Signed-in visitors redeem the code right
 * away; everyone else signs up or in first and the redeem runs as soon as the
 * session exists.
 */
export function JoinGroup() {
  const { code } = useParams<{ code: string }>();
  const user = useStore((s) => s.user);
  const joinGroup = useStore((s) => s.joinGroup);
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('joining');
  const [teamName, setTeamName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !code) return;

    void (async () => {
      try {
        const group = await joinGroup(code);
        setTeamName(group.name);
        setPhase('joined');
        // Brief pause so the confirmation is legible before the board appears.
        setTimeout(() => navigate(groupPath(group), { replace: true }), 900);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'That join code could not be used');
        setPhase('failed');
      }
    })();
  }, [user, code, joinGroup, navigate]);

  if (!user) return <Login joinCode={normalizeJoinCode(code ?? '')} />;

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="card animate-rise max-w-sm p-6 text-center">
        {phase === 'joining' && (
          <>
            <span className="mx-auto mb-3 block size-2.5 animate-pulse-dot rounded-full bg-accent" />
            <p className="text-sm text-ink-soft">Joining the team…</p>
          </>
        )}

        {phase === 'joined' && (
          <>
            <p className="text-sm font-medium text-done">You're in{teamName ? `: ${teamName}` : ''}.</p>
            <p className="mt-1 text-sm text-ink-faint">Taking you to the board…</p>
          </>
        )}

        {phase === 'failed' && (
          <>
            <p className="text-sm font-medium text-danger">Join code could not be used</p>
            <p className="mt-1 text-sm text-ink-faint">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="btn btn-ghost mt-4 w-full"
            >
              Continue to Project-Workflow
            </button>
          </>
        )}
      </div>
    </div>
  );
}
